/**
 * Grafiche di condivisione del Diario disegnate nel browser di chi legge.
 * Nessuna immagine generata da AI, nessun servizio esterno: solo la foto reale
 * dell'articolo (quando c'è), i colori e i caratteri del sito.
 */
import { mediaUrl } from "@/lib/api"; // eslint-disable-line no-unused-vars

const COLORS = {
  cream: "#F4F1EA",
  wood: "#1D3A2E",
  woodDeep: "#101F19",
  gold: "#C08E4A",
  sage: "#6E7A60",
};

export const FORMATS = {
  story: { width: 1080, height: 1920, label: "Storia", ratio: "9:16" },
  post: { width: 1080, height: 1350, label: "Post", ratio: "4:5" },
};

/** Foto dell'articolo già caricata nella pagina: la riusiamo senza una seconda richiesta. */
function pageImage(heroPath) {
  if (!heroPath) return null;
  const name = heroPath.split("/").pop().split("?")[0].replace(/\.\w+$/, "");
  // Solo immagini già pronte: mai attese, così la grafica esce sempre.
  return (
    [...document.images].find(
      (i) => (i.currentSrc || i.src || "").includes(name) && i.complete && i.naturalWidth > 0,
    ) || null
  );
}

/** Testo mandato a capo sulla larghezza disponibile, con numero massimo di righe. */
function wrap(ctx, text, maxWidth, maxLines) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines) break;
    } else {
      line = candidate;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 4) last = last.slice(0, -1);
    if (words.join(" ") !== lines.join(" ")) lines[maxLines - 1] = `${last.trim()}…`;
  }
  return lines;
}

function drawCover(ctx, img, x, y, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

function tracked(ctx, text, x, y, spacing) {
  let cursor = x;
  for (const char of text) {
    ctx.fillText(char, cursor, y);
    cursor += ctx.measureText(char).width + spacing;
  }
}

/** Disegna la card e restituisce il canvas. */
export function drawShareCard(article, format = "story", photoEl = null) {
  const { width, height } = FORMATS[format] || FORMATS.story;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // Nessuna attesa: i caratteri del sito sono già caricati dalla pagina che stiamo leggendo.
  // Usiamo la foto reale dell'articolo se è già nella pagina; altrimenti card tipografica.
  const photo =
    photoEl?.complete && photoEl.naturalWidth ? photoEl : pageImage(article.hero_image);
  const pad = 96;
  const inner = width - pad * 2;

  ctx.fillStyle = COLORS.wood;
  ctx.fillRect(0, 0, width, height);

  if (photo) {
    const photoH = Math.round(height * (format === "story" ? 0.5 : 0.44));
    drawCover(ctx, photo, 0, 0, width, photoH);
    const fade = ctx.createLinearGradient(0, photoH - 300, 0, photoH);
    fade.addColorStop(0, "rgba(16,31,25,0)");
    fade.addColorStop(0.75, "rgba(16,31,25,0.85)");
    fade.addColorStop(1, COLORS.wood);
    ctx.fillStyle = fade;
    ctx.fillRect(0, photoH - 300, width, 300);
    ctx.fillStyle = COLORS.wood;
    ctx.fillRect(0, photoH, width, height - photoH);
  } else {
    // Composizione tipografica: nessuna foto, solo il fondo del sito e un filo d'oro.
    const glow = ctx.createRadialGradient(width * 0.5, height * 0.28, 40, width * 0.5, height * 0.28, width * 0.9);
    glow.addColorStop(0, "rgba(110,122,96,0.35)");
    glow.addColorStop(1, "rgba(16,31,25,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);
  }

  // Intestazione: "Dal Diario"
  const footerY = height - pad - 78;
  const headerY = photo
    ? Math.round(height * (format === "story" ? 0.5 : 0.44)) + 92
    : Math.round(height * 0.28);
  ctx.fillStyle = COLORS.gold;
  ctx.font = "500 30px Outfit, system-ui, sans-serif";
  tracked(ctx, "DAL DIARIO", pad, headerY, 7);

  ctx.strokeStyle = "rgba(192,142,74,0.5)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(pad, headerY + 34);
  ctx.lineTo(pad + 140, headerY + 34);
  ctx.stroke();

  // Categoria
  ctx.fillStyle = "rgba(246,241,230,0.72)";
  ctx.font = "400 30px Outfit, system-ui, sans-serif";
  tracked(ctx, (article.category_name || "Diario dal Borgo").toUpperCase(), pad, headerY + 96, 4);

  // Titolo: le righe si adattano allo spazio davvero disponibile sopra la firma.
  const titleSize = format === "story" ? 84 : 72;
  const titleTop = headerY + 180;
  const available = footerY - 120 - titleTop;
  ctx.fillStyle = COLORS.cream;
  ctx.font = `500 ${titleSize}px "Cormorant Garamond", Georgia, serif`;
  const maxLines = Math.max(2, Math.floor(available / (titleSize * 1.16)));
  const lines = wrap(ctx, article.title, inner, maxLines);
  let y = titleTop + titleSize * 0.82;
  for (const line of lines) {
    ctx.fillText(line, pad, y);
    y += titleSize * 1.16;
  }

  // Estratto solo se resta spazio: la card deve respirare.
  if (article.excerpt && y + 130 < footerY - 90) {
    ctx.fillStyle = "rgba(246,241,230,0.7)";
    ctx.font = "300 36px Outfit, system-ui, sans-serif";
    const room = Math.floor((footerY - 90 - y) / 52);
    const ex = wrap(ctx, article.excerpt, inner, Math.min(room, format === "story" ? 3 : 2));
    y += 24;
    for (const line of ex) {
      ctx.fillText(line, pad, y);
      y += 52;
    }
  }

  // CTA
  ctx.fillStyle = COLORS.gold;
  ctx.font = "400 32px Outfit, system-ui, sans-serif";
  ctx.fillText("Scopri cosa si dice nel nostro Diario", pad, footerY - 46);

  // Firma
  ctx.strokeStyle = "rgba(246,241,230,0.22)";
  ctx.beginPath();
  ctx.moveTo(pad, footerY);
  ctx.lineTo(width - pad, footerY);
  ctx.stroke();

  ctx.fillStyle = COLORS.cream;
  ctx.font = '500 46px "Cormorant Garamond", Georgia, serif';
  ctx.fillText("Osteria Aterrana", pad, footerY + 62);

  ctx.fillStyle = "rgba(246,241,230,0.6)";
  ctx.font = "300 28px Outfit, system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("osteriaaterrana.it", width - pad, footerY + 60);
  ctx.textAlign = "left";

  return canvas;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

/** Conversione sincrona: evita callback asincrone del browser e attese inutili. */
export function dataUrlToBlob(dataUrl) {
  const [head, body] = dataUrl.split(",");
  const bytes = atob(body);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) buffer[i] = bytes.charCodeAt(i);
  return new Blob([buffer], { type: head.match(/:(.*?);/)[1] });
}
